const ReactGA = require('react-ga');
const ContentDefault = require('../constants/Content');
const LoggerDefault = require('./Logger');

const Content = ContentDefault.default;
const Logger = LoggerDefault.default;

const addSubscription = (plan) => {
  const subscriptionToAddData = {
    id: plan._id,
    name: `${plan.title} Subscription`,
    category: 'Subscription',
    variant: 'Unknown',
    price: plan.costWithCoupon || plan.actualCost,
    quantity: 1,
    sku: plan.months === 'INFINITY' ? 'non-bundled' : 'bundled',
  };

  ReactGA.plugin.execute('ec', 'addProduct', subscriptionToAddData);
};

const addProduct = (addedProduct, isVIPCart) => {
  const determinePrice = (details) => {
    if (details.promoItem && details.product.ownPromoData) {
      return details.product.ownPromoData.cost;
    }
    return details.amountToCharge || details.product.actualCost || 0;
  };

  const productToAddData = {
    id: addedProduct.product._id,
    name: addedProduct.product.name || 'Unknown',
    category: 'Shop',
    variant: 'Unknown',
    price: determinePrice(addedProduct),
    quantity: addedProduct.quantity || 1,
    sku: addedProduct.product._id || 'unknown',
    coupon: isVIPCart ? 'VIP' : 'NON-VIP',
  };
  ReactGA.plugin.execute('ec', 'addProduct', productToAddData);
};

// loop over and call the addProduct method
const addProducts = (productsAndDetails, isVIPCart) => {
  productsAndDetails.forEach((productsAndDetailsItem) => {
    addProduct(productsAndDetailsItem, isVIPCart);
  });
};

const addServiceAsProducts = (service, type, isVIPCart) => {
  const serviceToAddData = {
    id: type,
    name: type,
    category: 'Shop',
    variant: 'Unknown',
    price: service.cost,
    quantity: 1,
    sku: 'unknown',
    coupon: isVIPCart ? 'VIP' : 'NON-VIP',
  };
  ReactGA.plugin.execute('ec', 'addProduct', serviceToAddData);
};

const addProductsAndSubscription = (cartInfo, itemToAdd) => {
  const isVIPCart =
    cartInfo.subscriberDiscountAmount && cartInfo.subscriberDiscountAmount > 0;

  let itemToAddIsSubscription = false;

  // handles the case when a subscription plan is being added to the cart from Guest Checkout
  if (itemToAdd && itemToAdd.plan) {
    itemToAddIsSubscription = itemToAdd;
  }
  // pull subscription from the cartInfo object if itemToAdd does not exist
  else if (cartInfo.subscription && !itemToAdd) {
    itemToAddIsSubscription = cartInfo.subscription;
  }
  // handles the case when a subscription plan is being added to the cart as a User with an account
  // if planId exists in the itemToAdd object than pull the subscription from the cartInfo object
  else if (itemToAdd && itemToAdd.planId) {
    itemToAddIsSubscription = cartInfo.subscription;
  }

  if (itemToAddIsSubscription) {
    addSubscription(itemToAddIsSubscription.plan);
  }
  // itemToAdd does exist, then find that single product to add
  // To handle case where itemToAdd doesn't exist in the checkoutProcess
  else if (itemToAdd) {
    let addedProduct = itemToAdd;

    if (typeof itemToAdd.product === 'string') {
      addedProduct = cartInfo.productsAndDetails.find(
        (item) => item.product._id === itemToAdd.product,
      );
    }
    addProduct(addedProduct, isVIPCart);
  }

  if (
    !itemToAdd &&
    cartInfo.productsAndDetails &&
    cartInfo.productsAndDetails.length
  ) {
    addProducts(cartInfo.productsAndDetails, isVIPCart);
  }

  if (cartInfo.shippingInsurance) {
    const type = 'Shipping Insurance';
    let totalShippingInsuranceCost = cartInfo.shippingInsurance.cost;
    // itemToAddIsSubscription exists and the plan exists
    if (
      itemToAddIsSubscription &&
      itemToAddIsSubscription.plan &&
      itemToAddIsSubscription.plan.months !== 'INFINITY'
    ) {
      totalShippingInsuranceCost =
        cartInfo.shippingInsurance.cost * itemToAddIsSubscription.plan.months;
    }
    const shippingInsurance = {
      cost: totalShippingInsuranceCost,
    };
    addServiceAsProducts(shippingInsurance, type, isVIPCart);
  }

  if (cartInfo.isBrownBoxSelected) {
    const type = 'Discreet Shipping';
    const service = {
      cost: 3.99,
    };
    addServiceAsProducts(service, type, isVIPCart);
  }

  if (cartInfo.firstBoxShipping && cartInfo.firstBoxShipping.cost > 0) {
    const type = cartInfo.firstBoxShipping.title;
    addServiceAsProducts(cartInfo.firstBoxShipping, type, isVIPCart);
  }
};

// intended use -- when a user clicks on promo data
// in the cart, you can't click to view, you can only click to add, so it's  used when added
const onPromoClick = (promoProduct, position) => {
  const promoDataToAdd = {
    id: promoProduct._id,
    name: promoProduct.name || 'Unknown',
    creative: promoProduct.media[0].url || 'No image',
    position,
  };

  ReactGA.plugin.execute('ec', 'addPromo', promoDataToAdd);
  ReactGA.plugin.execute('ec', 'setAction', 'promo_click');
  ReactGA.event({
    category: 'Internal Promotions',
    action: 'click',
    label: promoDataToAdd.name,
  });
};

// intended use -- when a page loads with promo data on it
const viewPromo = (promoData) => {
  ReactGA.plugin.execute('ec', 'addPromo', promoData);
  ReactGA.event({
    category: 'Internal Promotions',
    action: 'view',
    label: promoData.position,
  });
};

const viewOrClickPromo = (promoData, viewOrClick, position) => {
  if (viewOrClick === 'click') {
    onPromoClick(promoData, position);
  } else {
    viewPromo(promoData);
  }
};

const transactionComplete = (gtmData, cartInfo) => {
  try {
    addProductsAndSubscription(cartInfo);

    ReactGA.plugin.execute('ec', 'setAction', 'purchase', {
      // Transaction ID. Required for purchases and refunds.
      id: gtmData.res.justPurchasedPurchaseId,
      affiliation: Content.names.fancyNamePlural,
      revenue: gtmData.total.toString(), // Total transaction value (incl. tax and shipping)
      tax: gtmData.taxesAndFees.toString(),
      shipping: gtmData.shipping.toString(),
      paymentType: gtmData.paymentType,
      coupon:
        cartInfo.coupon && cartInfo.coupon.code ? cartInfo.coupon.code : '',
    });
    // With the ec plugin, a pageview is what sends the data across the network.
    ReactGA.event({
      category: 'Checkout',
      action: 'Checkout Complete',
      label: gtmData.res.justPurchasedPurchaseId,
    });
  } catch (e) {
    Logger.error(e);
  }
};

const paymentRequestStatus = (paymentRequestAvailable) => {
  try {
    ReactGA.event({
      category: 'feature-paymentMethods',
      action: paymentRequestAvailable ? 'applePayEnabled' : 'applePayDisabled',
    });
  } catch (e) {
    Logger.error(e);
  }
};

const checkoutProcess = (cartInfo, stepNum) => {
  try {
    addProductsAndSubscription(cartInfo);
    ReactGA.plugin.execute('ec', 'setAction', 'checkout', { step: stepNum });

    ReactGA.event({
      category: 'Checkout',
      action: 'checkout process',
    });
  } catch (e) {
    Logger.error(e);
  }
};

const viewProduct = (data, isProduct, isPromo, position) => {
  try {
    if (!isProduct) {
      const subscriptionToAddData = {
        id: data._id,
        name: `${data.title} Subscription`,
        category: 'Subscription',
        variant: 'Unknown',
        price: data.costWithCoupon || data.actualCost,
        quantity: 1,
      };
      ReactGA.plugin.execute('ec', 'addProduct', subscriptionToAddData);

      ReactGA.plugin.execute('ec', 'setAction', 'detail');

      ReactGA.event({
        category: 'Subscription',
        action: 'view subscription',
      });
    }

    if (isProduct) {
      if (isPromo) {
        viewOrClickPromo(data, 'click', position);
      }

      const productDataToAdd = {
        id: data._id,
        name: data.name || 'Unknown',
        category: 'Shop',
        variant: 'Unknown',
        price: data.actualCost ? data.actualCost : data.originalCost || 0,
        quantity: 1,
      };

      ReactGA.plugin.execute('ec', 'addProduct', productDataToAdd);

      ReactGA.plugin.execute('ec', 'setAction', 'detail');

      ReactGA.event({
        category: 'Shop',
        action: 'view product',
      });
    }
  } catch (e) {
    Logger.error(e);
  }
};

const addToCart = (cartInfo, itemToAdd, listName) => {
  try {
    addProductsAndSubscription(cartInfo, itemToAdd);

    ReactGA.plugin.execute('ec', 'setAction', 'add', { list: listName });

    ReactGA.event({
      category: 'Cart',
      action: 'add to cart',
      label: listName,
    });
  } catch (e) {
    Logger.error(e);
  }
};

const breastPumpProcess = (stepNum) => {
  try {
    ReactGA.event({
      category: 'Breast Pumps',
      action: `Step ${stepNum} Submitted`,
    });
  } catch (e) {
    Logger.error(e);
  }
};

const clickInsurancePartnerLink = (partnerName) => {
  try {
    ReactGA.event({
      category: 'Breast Pumps',
      action: 'Partner Clicked',
      label: partnerName,
    });
  } catch (e) {
    Logger.error(e);
  }
};

const clickUpdateCreditCard = (hasError) => {
  try {
    ReactGA.event({
      category: 'MyAccount',
      action: 'updateCreditCard',
      label: `updateCreditCard-hasError-${!!hasError}`,
    });
  } catch (e) {
    Logger.error(e);
  }
};

const clickMyAccountDropdown = (dropdownItem, hasError) => {
  try {
    ReactGA.event({
      category: 'MyAccount',
      action: dropdownItem,
      label: `${dropdownItem}-hasError-${!!hasError}`,
    });
  } catch (e) {
    Logger.error(e);
  }
};

const accountDetailsUpdated = (page) => {
  try {
    ReactGA.event({
      category: 'MyAccount',
      action: page,
    });
  } catch (e) {
    Logger.error(e);
  }
};

const breastPumpPartnerApiInfoSent = (partnerName) => {
  try {
    ReactGA.event({
      category: 'Breast Pumps',
      action: 'BP Api Lead Sent',
      label: partnerName,
    });
  } catch (e) {
    Logger.error(e);
  }
};

const registryProcess = (stepNum) => {
  try {
    ReactGA.event({
      category: 'Registry',
      action: `Step ${stepNum} Submitted`,
    });
  } catch (e) {
    Logger.error(e);
  }
};

const sendSponsorInfo = (sponsor) => {
  try {
    ReactGA.event({
      category: 'Data Partner',
      action: 'Offer Selected',
      label: sponsor,
    });
  } catch (e) {
    Logger.error(e);
  }
};

const addShippingInsurance = () => {
  try {
    ReactGA.event({
      category: 'Shipping Insurance',
      action: 'Purchased',
    });
  } catch (e) {
    Logger.error(e);
  }
};

const optedIntoSMS = () => {
  try {
    ReactGA.event({
      category: 'Opted-In SMS',
      action: 'Opt-in Selected',
    });
  } catch (e) {
    Logger.error(e);
  }
};

const clickedTrackingNumber = () => {
  try {
    ReactGA.event({
      category: 'My Order',
      action: 'Clicked Tracking Number',
    });
  } catch (e) {
    Logger.error(e);
  }
};

const corpGiftingSubmit = () => {
  try {
    ReactGA.event({
      category: 'CorporateGifting',
      action: 'LeadSubmitted',
    });
  } catch (e) {
    Logger.error(e);
  }
};

const dueDateCalcSubmitted = () => {
  try {
    ReactGA.event({
      category: 'Due Date Calc',
      action: 'Submitted',
    });
  } catch (e) {
    Logger.error(e);
  }
};

const dueDateCalcOptedIntoEmail = () => {
  try {
    ReactGA.event({
      category: 'Due Date Calc',
      action: 'Opted in to email',
    });
  } catch (e) {
    Logger.error(e);
  }
};

const scheduledCancel = () => {
  try {
    ReactGA.event({
      category: 'MyAccount',
      action: 'Scheduled Cancel/Complete',
    });
  } catch (e) {
    Logger.error(e);
  }
};

const extendedSubscription = () => {
  try {
    ReactGA.event({
      category: 'MyAccount',
      action: 'Extended Subscription',
    });
  } catch (e) {
    Logger.error(e);
  }
};

const editPaymentUsed = () => {
  try {
    ReactGA.event({
      category: 'Edit Payment',
      action: 'Updated Credit Card',
    });
  } catch (e) {
    Logger.error(e);
  }
};

const giveawayEntry = () => {
  try {
    ReactGA.event({
      category: 'Giveaway',
      action: 'New Giveaway Entry',
    });
  } catch (e) {
    Logger.error(e);
  }
};

const lifeInsuranceEntry = (state) => {
  try {
    ReactGA.event({
      category: 'Insurance',
      action: 'Step 1 Submitted',
      label: state,
    });
  } catch (e) {
    Logger.error(e);
  }
};

const clickedReferralButton = (buttonClicked) => {
  try {
    ReactGA.event({
      category: 'Refer-A-Friend',
      action: 'Share Clicked',
      label: buttonClicked,
    });
  } catch (e) {
    Logger.error(e);
  }
};

module.exports = {
  transactionComplete,
  paymentRequestStatus,
  checkoutProcess,
  viewProduct,
  addToCart,
  onPromoClick,
  viewPromo,
  viewOrClickPromo,
  breastPumpProcess,
  clickInsurancePartnerLink,
  clickUpdateCreditCard,
  clickMyAccountDropdown,
  accountDetailsUpdated,
  breastPumpPartnerApiInfoSent,
  registryProcess,
  sendSponsorInfo,
  addShippingInsurance,
  optedIntoSMS,
  clickedTrackingNumber,
  corpGiftingSubmit,
  dueDateCalcSubmitted,
  dueDateCalcOptedIntoEmail,
  scheduledCancel,
  extendedSubscription,
  editPaymentUsed,
  giveawayEntry,
  lifeInsuranceEntry,
  clickedReferralButton,
};
